import { useState, useEffect, useRef, useCallback } from 'react';

const APP_VERSION = import.meta.env.VITE_APP_VERSION || '1.0.0';

interface UseAppVersionReturn {
  needsUpdate: boolean;
  currentVersion: string;
  storedVersion: string | null;
  clearCacheAndReload: () => Promise<void>;
  dismissUpdate: () => void;
}


export const useAppVersion = (): UseAppVersionReturn => {
  const [needsUpdate, setNeedsUpdate] = useState(false);
  const [storedVersion, setStoredVersion] = useState<string | null>(null);
  const [currentVersion, setCurrentVersion] = useState<string>(APP_VERSION);
  const isClearingCache = useRef(false);

  const clearAllCacheAndStorage = useCallback(async (versionToStore: string) => {
    if (isClearingCache.current) {
      return; // Prevent multiple simultaneous clears
    }
    
    isClearingCache.current = true;
    
    try {
      // Clear localStorage (except version - we'll update it)
      const keysToKeep: string[] = [];
      const keysToRemove: string[] = [];
      
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && !keysToKeep.includes(key)) {
          keysToRemove.push(key);
        }
      }
      
      keysToRemove.forEach(key => localStorage.removeItem(key));

      // Clear sessionStorage
      sessionStorage.clear();

      // Unregister service worker to ensure fresh version is loaded
      if ('serviceWorker' in navigator) {
        try {
          const registrations = await navigator.serviceWorker.getRegistrations();
          await Promise.all(registrations.map(registration => registration.unregister()));
          console.log('Service workers unregistered');
        } catch (swError) {
          console.warn('Error unregistering service workers:', swError);
        }
      }

      // Clear all caches
      if ('caches' in window) {
        try {
          const cacheKeys = await caches.keys();
          await Promise.all(cacheKeys.map(key => caches.delete(key)));
          console.log('All caches cleared');
        } catch (cacheError) {
          console.warn('Error clearing caches:', cacheError);
        }
      }

      // Update version with the latest from server
      localStorage.setItem('app_version', versionToStore);
      // Remove dismissed flag since we're updating
      localStorage.removeItem('update-dismissed-version');

      // Force reload with cache bypass
      setCurrentVersion(APP_VERSION)
      setTimeout(() => {
        // Use window.location.reload with cache bypass
        window.location.href = window.location.href.split('?')[0] + '?v=' + versionToStore + '&t=' + Date.now();
      }, 1000);
    } catch (error) {
      console.error('Error clearing cache:', error);
      isClearingCache.current = false;
    }
  }, []);

  useEffect(() => {
    const stored = localStorage.getItem('app_version');
    setStoredVersion(stored);

    if (stored === null) {
      // First-time visitor: store current version, do not show update modal
      localStorage.setItem('app_version', APP_VERSION);
      setStoredVersion(APP_VERSION);
      return;
    }

    // Returning visitor: show update modal only when deployed version is newer than what they had
    if (stored !== APP_VERSION && APP_VERSION) {
      setNeedsUpdate(true);
    }
  }, []);


  const clearCacheAndReload = async () => {
      await clearAllCacheAndStorage(APP_VERSION);
  };

  const dismissUpdate = () => {
    setNeedsUpdate(false);
  };

  return {
    needsUpdate,
    currentVersion,
    storedVersion,
    clearCacheAndReload,
    dismissUpdate,
  };
};

